import React from 'react';
import ToneAnalysisChart from '../components/tone-analysis';
import SentimentGauge from '../components/sentiment-analysis';
import { Loader2 } from 'lucide-react';

interface ConversationDynamicsTabProps {
  isLoading: boolean;
  conversationDynamics?: any;
}

export const ConversationDynamicsTab: React.FC<
  ConversationDynamicsTabProps
> = ({ isLoading, conversationDynamics }) => {
  // If real data is provided, use it, otherwise use sample data
  const dynamics = conversationDynamics || {
    engagement: {
      score: 4.0,
      description:
        'The conversation was low in engagement despite an equal share of dialogue, as you dominated the discussion while Henry remained passive, mostly reacting rather than contributing. The lack of open-ended questions and interactive discussion made the review feel one-sided and transactional rather than a collaborative growth conversation.',
      whatToImprove:
        'Encourage more dialogue by asking open-ended questions and prompting Henry to reflect on his challenges and goals. For example, ask open-ended questions like "How do you feel about this feedback?" or "What situations do you find difficult when it comes to speaking up?"',
    },
    sentimentTone: {
      sentiment: {
        score: -10,
        label: 'Slightly negative',
        description:
          'The sentiment was slightly negative (-10) because, while not harsh, the feedback lacked warmth, encouragement, and clear direction, making it feel transactional. The hesitant tone and vague phrasing may have left Henry uncertain about his performance and next steps.',
      },
      tone: {
        distribution: {
          confident: 20,
          neutral: 30,
          hesitant: 50,
        },
        description:
          'Your tone was mostly hesitant (50%), with frequent filler words and an unstructured delivery that weakened clarity. While 30% of the tone was neutral, it lacked warmth, and only 20% showed confidence, making moments of clear direction inconsistent.',
      },
      whatToImprove:
        'To make the conversation clearer, more engaging, and motivating, consider reducing filler words, structuring feedback in advance, and adopting a more confident, supportive tone. Balancing constructive criticism with positive reinforcement and actionable next steps could also help create a more effective discussion.',
    },
  };

  const renderEngagementScore = (score: number) => {
    // Create an array of 10 elements for the score dots
    return (
      <div className="flex flex-row pb-4">
        <div className="text-3xl font-semibold">
          {dynamics.engagement.score.toFixed(1)}
        </div>
        <div className="flex flex-1 gap-1 pl-5 pb-2 items-end">
          {Array.from({ length: 10 }).map((_, index) => (
            <div
              key={index}
              className={`h-2 flex-1 rounded-full ${
                index < Math.floor(score) ? 'bg-[#FF9A42]' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderLegend = (type: 'sentiment' | 'tone') => {
    const items =
      type === 'sentiment'
        ? [
            { color: '#EF4444', label: 'Negative' },
            { color: '#FF9A42', label: 'Neutral' },
            { color: '#10B981', label: 'Positive' },
          ]
        : [
            { color: '#10B981', label: 'Confident' },
            { color: '#3B82F6', label: 'Neutral' },
            { color: '#FF9A42', label: 'Hesitant' },
          ];

    return (
      <div className="flex justify-center gap-4 mt-2">
        {items.map((item, index) => (
          <div key={index} className="flex items-center">
            <div
              className="w-3 h-3 rounded-sm mr-1"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-sm text-gray-600">{item.label}</span>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="p-6 flex flex-col justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <span className="mt-4 text-gray-600">
          Loading conversation dynamics analysis...
        </span>
      </div>
    );
  }

  // If there's no data yet, return a placeholder
  if (!conversationDynamics) {
    return (
      <div className="p-6">
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <p className="text-gray-500">
            Conversation dynamics analysis not available for this meeting.
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Please select a different tab or try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Engagement Section */}
      <div className="mb-8 p-6 border border-gray-200 rounded-lg">
        <h3 className="text-m font-medium mb-2">Engagement</h3>

        <div className="my-3">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">Score</div>
          </div>
          {renderEngagementScore(dynamics.engagement.score)}
        </div>

        <p className="text-gray-700 text-sm leading-relaxed mb-4">
          {dynamics.engagement.description}
        </p>

        <div>
          <h4 className="font-medium text-sm mb-2">What to improve</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            {dynamics.engagement.whatToImprove}
          </p>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-6">
        {/* Two Column Layout for Sentiment and Tone */}
        <div className="grid md:grid-cols-2 gap-6 relative">
          {/* Sentiment */}
          <div className="pr-6 md:border-r border-gray-200">
            <h3 className="text-m font-medium mb-4">Sentiment</h3>

            <SentimentGauge score={dynamics.sentimentTone.sentiment.score} />
            {renderLegend('sentiment')}

            <p className="text-gray-700 text-sm leading-relaxed mt-4 mb-6">
              {dynamics.sentimentTone.sentiment.description}
            </p>
          </div>

          {/* Tone */}
          <div className="pl-0 md:pl-6">
            <h3 className="text-m font-medium mb-4">Tone</h3>
            <ToneAnalysisChart
              confident={dynamics.sentimentTone.tone.distribution.confident}
              neutral={dynamics.sentimentTone.tone.distribution.neutral}
              hesitant={dynamics.sentimentTone.tone.distribution.hesitant}
            />

            {renderLegend('tone')}

            <p className="text-gray-700 text-sm leading-relaxed mt-4">
              {dynamics.sentimentTone.tone.description}
            </p>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-sm mb-2 mt-4">What to improve</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            {dynamics.sentimentTone.whatToImprove}
          </p>
        </div>
      </div>
    </div>
  );
};
